import React, { useState, useEffect } from "react";

const MobileReservationModal = ({ tables, shifts, selectedDate, onClose, onSuccess, initialTable = null }) => {
  const [selectedTable, setSelectedTable] = useState(initialTable?.id || "");
  const [name, setName] = useState("");
  const [people, setPeople] = useState("1");
  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");
  const [note, setNote] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [selectedShift, setSelectedShift] = useState("");
  const [overCapacityWarning, setOverCapacityWarning] = useState(false);
  const [overCapacityDiff, setOverCapacityDiff] = useState(0);

  useEffect(() => {
    if (shifts.length > 0 && !selectedShift) {
      const evening = shifts.find(s =>
        s.name.toLowerCase().includes("večer") ||
        s.name.toLowerCase().includes("vecer") ||
        s.name.toLowerCase().includes("evening")
      );
      setSelectedShift(String(evening?.id || shifts[shifts.length - 1]?.id || shifts[0]?.id));
    }
  }, [shifts]);

  const d = new Date(selectedDate);
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  const [reservationDate, setReservationDate] = useState(`${yyyy}-${mm}-${dd}`);

  const indoor = tables.filter(t => !t.is_outdoor).sort((a, b) => a.label.localeCompare(b.label));
  const outdoor = tables.filter(t => t.is_outdoor).sort((a, b) => a.label.localeCompare(b.label));

  const handleSubmit = async () => {
    setError("");

    if (!selectedTable) return setError("Odaberi stol!");
    if (!name.trim()) return setError("Unesi ime gosta!");

    const tableObj = tables.find(t => t.id === Number(selectedTable));
    if (!tableObj) return setError("Stol ne postoji!");

    const peopleNum = parseInt(people) || 1;

    let finalNote = note || "";

    if (peopleNum > tableObj.seats) {
      const diff = peopleNum - tableObj.seats;
      const extraNote = `Potrebno ${diff} dodatnih stolica.`;

      finalNote = finalNote
        ? `${finalNote} | ${extraNote}`
        : extraNote;
    }

    if (peopleNum > tableObj.seats && !overCapacityWarning) {
      setOverCapacityDiff(peopleNum - tableObj.seats);
      setOverCapacityWarning(true);
      return;
    }

    if (selectedShift === "manual" && (!startTime || !endTime))
      return setError("Unesi početak i kraj rezervacije!");

    setLoading(true);

    const body = {
      table_id: Number(selectedTable),
      guest_name: name,
      guests: peopleNum,
      reservation_date: reservationDate,
      shift_id: selectedShift !== "manual" ? selectedShift : null,
      note: finalNote,
    };

    if (selectedShift === "manual") {
      body.start_datetime = `${reservationDate}T${startTime}:00`;
      body.end_datetime = `${reservationDate}T${endTime}:00`;
    }

    if (selectedShift !== "manual") {
      const shift = shifts.find(s => String(s.id) === String(selectedShift));
      if (shift && reservationDate === new Date().toLocaleDateString("en-CA")) {
        const now = new Date();
        const nowMinutes = now.getHours() * 60 + now.getMinutes();
        const [h, m] = shift.start_time.split(":").map(Number);
        if ((h * 60 + m) < nowMinutes) {
          setError("Odabrani termin je već počeo ili završio!");
          setLoading(false);
          return;
        }
      }
    }

    try {
      const response = await fetch("/reservations/api/reservations/create/", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const data = await response.json();
      if (response.ok) {
        setOverCapacityWarning(false);
        onSuccess();
      } else {
        setError(data.error || "Greška na serveru.");
      }
    } catch {
      setError("Mrežna greška – pokušaj ponovo.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-80 z-50 flex flex-col">
      <div className="bg-gray-900 border-b border-[#c1b428] px-4 py-3 flex justify-between items-center">
        <h2 className="text-[#c1b428] font-bold text-base">Nova rezervacija</h2>
        <button onClick={onClose} className="text-gray-400 text-2xl leading-none">✕</button>
      </div>

      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4">

        {error && (
          <div className="p-3 bg-red-900 border border-[#c1b428] rounded-lg text-[#c1b428] text-sm">
            {error}
          </div>
        )}

        {/* Stol */}
        <div>
          <label className="block text-[#c1b428] text-sm font-semibold mb-1">Stol:</label>
          <select
            value={selectedTable}
            onChange={e => {
              setSelectedTable(e.target.value);
              setOverCapacityWarning(false);
            }}
            className="w-full bg-gray-800 text-white border border-[#c1b428] rounded-lg px-3 py-3 text-sm"
          >
            <option value="">— Odaberi stol —</option>
            {indoor.length > 0 && (
              <optgroup label="🏠 Unutarnji stolovi">
                {indoor.map(t => (
                  <option key={t.id} value={t.id}>{t.label} ({t.seats} mjesta)</option>
                ))}
              </optgroup>
            )}
            {outdoor.length > 0 && (
              <optgroup label="🌿 Vanjski stolovi">
                {outdoor.map(t => (
                  <option key={t.id} value={t.id}>{t.label} ({t.seats} mjesta)</option>
                ))}
              </optgroup>
            )}
          </select>
        </div>

        {/* Ime */}
        <div>
          <label className="block text-[#c1b428] text-sm font-semibold mb-1">Ime gosta:</label>
          <input
            type="text"
            value={name}
            onChange={e => setName(e.target.value)}
            className="w-full bg-gray-800 text-white border border-[#c1b428] rounded-lg px-3 py-3 text-sm"
            placeholder="Ime i prezime"
          />
        </div>

        {/* Broj osoba */}
        <div>
          <label className="block text-[#c1b428] text-sm font-semibold mb-1">Broj osoba:</label>
          <input
            type="number"
            value={people}
            onChange={e => {
              setPeople(e.target.value);
              setOverCapacityWarning(false);
            }}
            onBlur={e => {
              const val = parseInt(e.target.value);
              if (!val || val < 1) setPeople("1");
              else setPeople(String(val));
            }}
            min="1"
            inputMode="numeric"
            className="w-full bg-gray-800 text-white border border-[#c1b428] rounded-lg px-3 py-3 text-sm"
          />
        </div>

        {/* Datum */}
        <div>
          <label className="block text-[#c1b428] text-sm font-semibold mb-1">Datum:</label>
          <input
            type="date"
            value={reservationDate}
            onChange={e => setReservationDate(e.target.value)}
            className="w-full bg-gray-800 text-[#c1b428] border border-[#c1b428] rounded-lg px-3 py-3 text-sm"
          />
        </div>

        {/* Termin */}
        <div>
          <label className="block text-[#c1b428] text-sm font-semibold mb-1">Termin:</label>
          <select
            value={selectedShift}
            onChange={e => setSelectedShift(e.target.value)}
            className="w-full bg-gray-800 text-white border border-[#c1b428] rounded-lg px-3 py-3 text-sm"
          >
            {shifts.map(s => (
              <option key={s.id} value={s.id}>
                {s.name} ({s.start_time?.slice(0, 5)} – {s.end_time?.slice(0, 5)})
              </option>
            ))}
            <option value="manual">Ručno</option>
          </select>
        </div>

        {/* Ručno vrijeme */}
        {selectedShift === "manual" && (
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[#c1b428] text-xs mb-1">Od:</label>
              <input
                type="time"
                value={startTime}
                onChange={e => setStartTime(e.target.value)}
                className="w-full bg-gray-800 text-white border border-[#c1b428] rounded-lg px-3 py-3 text-sm"
              />
            </div>
            <div>
              <label className="block text-[#c1b428] text-xs mb-1">Do:</label>
              <input
                type="time"
                value={endTime}
                onChange={e => setEndTime(e.target.value)}
                className="w-full bg-gray-800 text-white border border-[#c1b428] rounded-lg px-3 py-3 text-sm"
              />
            </div>
          </div>
        )}

        {/* Napomena */}
        <div>
          <label className="block text-[#c1b428] text-sm font-semibold mb-1">Napomena:</label>
          <textarea
            rows="2"
            value={note}
            onChange={e => setNote(e.target.value)}
            placeholder="Opcionalno..."
            className="w-full bg-gray-800 text-white border border-[#c1b428] rounded-lg px-3 py-3 text-sm"
          />
        </div>
      </div>

      {overCapacityWarning && (
        <div className="p-3 bg-yellow-900 border border-yellow-500 rounded-lg text-yellow-300 text-sm">
          <p className="font-bold mb-1">
            ⚠️ Previše osoba za stol!
          </p>

          <p className="mb-2">
            Stol{" "}
            {tables.find(t => t.id === Number(selectedTable))?.label} ima{" "}
            {tables.find(t => t.id === Number(selectedTable))?.seats} mjesta,
            a uneseno je {people} osoba.
            Potrebno je {overCapacityDiff} dodatnih stolica.
          </p>

          <p className="text-xs mb-3">
            Napomena će biti automatski dodana:
            <span className="italic">
              {" "}Potrebno {overCapacityDiff} dodatnih stolica.
            </span>
          </p>

          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setOverCapacityWarning(false)}
              className="flex-1 py-2 bg-gray-700 text-white rounded-lg text-sm"
            >
              Izmijeni
            </button>

            <button
              type="button"
              onClick={handleSubmit}
              className="flex-1 py-2 bg-yellow-600 text-black rounded-lg font-bold text-sm"
            >
              Ipak rezerviraj
            </button>
          </div>
        </div>
      )}

      {/* Footer gumbi */}
      <div className="px-4 py-4 bg-gray-900 border-t border-gray-700 flex gap-3">
        <button
          onClick={onClose}
          className="flex-1 py-3 bg-gray-700 text-white rounded-lg font-semibold text-sm"
        >
          Odustani
        </button>
        <button
          onClick={handleSubmit}
          disabled={loading || overCapacityWarning}
          className={`flex-1 py-3 bg-[#c1b428] text-black rounded-lg font-bold text-sm
            ${(loading || overCapacityWarning) ? "opacity-50 cursor-not-allowed" : ""}`}
        >
          {loading ? "Spremanje..." : "Spremi"}
        </button>
      </div>
    </div>
  );
};

export default MobileReservationModal;